export const getPrepaidSummary = (customer: any) => {
  if (!customer) return { availableStringings: 0, availableAmount: 0 };
  const currentPrepaid =
    (typeof customer.prepaid === "string"
      ? JSON.parse(customer.prepaid)
      : customer.prepaid) || [];

  let availableStringings = 0;
  let availableAmount = 0;

  currentPrepaid.forEach((p: any) => {
    availableStringings += parseInt(p.numStringings || "0") - (p.usedStringings || 0);
    availableAmount += parseFloat((p.amount?.toString() || "0").replace(",", ".")) - (p.usedAmount || 0);
  });

  return { availableStringings, availableAmount, currentPrepaid };
};

export const handlePrepaidDeduction = async (
  customers: any[],
  customerName: string,
  targetJobs: any[],
  API_URL: string,
  getAuthHeader: () => any,
  customerId?: string
): Promise<{ continueWithManualPayment: boolean; jobsToMarkPaid: any[]; aborted?: boolean }> => {
  const unpaidJobs = targetJobs.filter((j) => !j.paid);
  if (unpaidJobs.length === 0) return { continueWithManualPayment: true, jobsToMarkPaid: [] };

  const activeC = customers?.find((c: any) => 
    (customerId && (c.id === customerId || c.numericId?.toString() === customerId?.toString())) || 
    (c.name || "").trim().toLowerCase() === (customerName || "").trim().toLowerCase() ||
    (c.name || "").trim().toLowerCase().startsWith((customerName || "").trim().toLowerCase())
  );
  if (!activeC) return { continueWithManualPayment: true, jobsToMarkPaid: unpaidJobs };

  const { availableStringings, availableAmount, currentPrepaid } = getPrepaidSummary(activeC);

  if (availableStringings > 0 || availableAmount > 0) {
    let deductJobsCount = 0;
    let deductValue = 0;
    const jobsCovered: any[] = [];
    
    // Simulate what we can pay for
    let tempStr = availableStringings;
    let tempAmt = availableAmount;
    
    for (const job of unpaidJobs) {
      if (tempStr > 0) {
        tempStr--;
        deductJobsCount++;
        jobsCovered.push(job);
      } else if (tempAmt > 0 && job.price != null && tempAmt >= job.price) {
        tempAmt -= job.price;
        deductValue += job.price;
        jobsCovered.push(job);
      }
    }

    if (jobsCovered.length > 0) {
      let promptMsg = `O cliente possui saldo pré-pago ativo.\n`;
      if (availableStringings > 0) promptMsg += `Encordoamentos restantes: ${availableStringings}\n`;
      if (availableAmount > 0) promptMsg += `Valor restante: R$ ${availableAmount.toFixed(2)}\n`;
      
      promptMsg += `\nDeseja utilizar este saldo para abater ${jobsCovered.length} item(ns) da ordem?`;
      if (deductJobsCount > 0) promptMsg += `\n- ${deductJobsCount} encordoamento(s)`;
      if (deductValue > 0) promptMsg += `\n- R$ ${deductValue.toFixed(2)}`;
      
      const usePrepaid = window.confirm(promptMsg);

      if (usePrepaid) {
        let remainingStr = deductJobsCount;
        let remainingAmt = deductValue;

        for (let i = 0; i < currentPrepaid.length; i++) {
          const p = currentPrepaid[i];
          // Deduct stringings
          if (remainingStr > 0) {
            const pAvailableStr = Math.max(0, parseInt(p.numStringings || "0") - (p.usedStringings || 0));
            if (pAvailableStr > 0) {
              const deduct = Math.min(pAvailableStr, remainingStr);
              currentPrepaid[i].usedStringings = (p.usedStringings || 0) + deduct;
              remainingStr -= deduct;
            }
          }
          // Deduct amount
          if (remainingAmt > 0) {
            const pAvailableAmt = Math.max(0, parseFloat((p.amount?.toString() || "0").replace(",", ".")) - (p.usedAmount || 0));
            if (pAvailableAmt > 0) {
              const deduct = Math.min(pAvailableAmt, remainingAmt);
              currentPrepaid[i].usedAmount = (p.usedAmount || 0) + deduct;
              remainingAmt -= deduct;
            }
          }
        }

        activeC.prepaid = currentPrepaid;
        try {
          const res = await fetch(`${API_URL}/api/customers/${activeC.id}`, {
            method: "PUT",
            headers: {
              ...getAuthHeader(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ...activeC, prepaid: currentPrepaid }),
          });
          
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || errData.details || res.statusText || "Erro desconhecido");
          }

          return {
            continueWithManualPayment: false,
            jobsToMarkPaid: jobsCovered,
          };
        } catch (err: any) {
          console.error("Erro ao abater pré-pago:", err);
          alert(`Erro ao dar baixa no pré-pago: ${err.message}`);
          return { continueWithManualPayment: true, jobsToMarkPaid: unpaidJobs };
        }
      } else {
        return { continueWithManualPayment: false, jobsToMarkPaid: [], aborted: true };
      }
    }
  }
  
  return { continueWithManualPayment: true, jobsToMarkPaid: unpaidJobs };
};


export const refundPrepaidDeduction = async (
  customers: any[],
  targetJobs: any[],
  API_URL: string,
  getAuthHeader: () => any
) => {
  if (!targetJobs || targetJobs.length === 0) return;
  const job = targetJobs[0];
  const customerId = job.customerId;
  const customerName = job.customerName;

  let activeC = customers.find(
    (c: any) =>
      (customerId && (c.id === customerId || c.numericId?.toString() === customerId?.toString())) ||
      (c.name || "").trim().toLowerCase() === (customerName || "").trim().toLowerCase() ||
      (c.name || "").trim().toLowerCase().startsWith((customerName || "").trim().toLowerCase())
  );

  if (!activeC) return;

  const currentPrepaid = (typeof activeC.prepaid === "string" ? JSON.parse(activeC.prepaid) : activeC.prepaid) || [];
  if (!Array.isArray(currentPrepaid) || currentPrepaid.length === 0) return;

  let anyRefund = false;
  for (const j of targetJobs) {
    const originalData = typeof j.originalData === "string" ? JSON.parse(j.originalData || '{}') : (j.originalData || {});
    if (originalData.paymentMethod === "PREPAID") {
      anyRefund = true;
      let refunded = false;
      for (let i = currentPrepaid.length - 1; i >= 0; i--) {
        const p = currentPrepaid[i];
        if ((p.usedStringings || 0) > 0) {
          currentPrepaid[i].usedStringings -= 1;
          refunded = true;
          break;
        }
      }
      
      if (!refunded && j.price != null) {
        let amtToRefund = typeof j.price === 'string' ? parseFloat(j.price) : j.price;
        for (let i = currentPrepaid.length - 1; i >= 0; i--) {
          const p = currentPrepaid[i];
          if ((p.usedAmount || 0) > 0) {
            const refund = Math.min(p.usedAmount, amtToRefund);
            currentPrepaid[i].usedAmount -= refund;
            amtToRefund -= refund;
            if (amtToRefund <= 0) break;
          }
        }
      }
    }
  }

  if (anyRefund) {
    activeC.prepaid = currentPrepaid;
    try {
      await fetch(`${API_URL}/api/customers/${activeC.id}`, {
        method: "PUT",
        headers: { ...getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...activeC }),
      });
    } catch (e) {
      console.error("Failed to refund prepaid", e);
    }
  }
};
